import React, { useCallback, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { renderToStaticMarkup } from 'react-dom/server';
import ServerSideList from '../../../../../../commonComponents/lists/ServerSideList';
import { formatDate } from '../../../../../../services/dateUtilityFunctions';
import translate from '../../../../../../commonComponents/translate';
import ConfigsContext from '../../../../../../contextProviders/ConfigsContext';

const renderFromInformation = (t, item) => {
  const {
    origin,
    game,
    journey,
    survey,
    session,
    marketOrder,
    academyOrder,
    marketCommission,
    marketCommissionReport,
    marketCommissionCryptoReport,
  } = item;

  switch (origin) {
    case 'marketCommission':
      return t(
        'marketCommission.and.name',
        marketCommission?.date
          ? formatDate(marketCommission.date)
          : t('commission.not.found'),
      );
    case 'marketCommissionDailyForecast':
      return t('daily.forecast');
    case 'marketCommissionReport':
      return t(
        'marketCommissionReport.and.name',
        marketCommissionReport?.date
          ? formatDate(marketCommissionReport.date)
          : t('report.not.found'),
      );
    case 'marketCommissionCryptoReport':
      return t(
        'marketCommissionCryptoReport.and.name',
        marketCommissionCryptoReport?.date
          ? formatDate(marketCommissionCryptoReport.date)
          : t('report.crypto.not.found'),
      );
    case 'withdrawRequest':
      return t('commissions.transactionsList.withdrawRequest');
    case 'custom':
      return t('custom');
    case 'game':
      return t('game.and.name', game?.name || '-');
    case 'tip':
      return t('rewards_tips');
    case 'survey':
      return t('survey.and.name', survey?.title || '-');
    case 'session':
      return t('session.and.name', session?.name || '-');
    case 'marketPlace':
      return academyOrder?.number
        ? t('academyOrder.and.name', academyOrder?.number || '-')
        : t('marketOrder.and.name', marketOrder?.number || '-');
    case 'commissionPlan':
      return t('commissionPlan');
    case 'journey':
      return t('journey.and.name', journey?.title || '-');
    default:
      return 'Type not supported';
  }
};

function CoinsEvolution(props) {
  const { t, filters } = props;

  const { organization } = useContext(ConfigsContext);
  const { enabled: isCharityCoinsEnabled } = organization?.services?.charity;

  const columns = useMemo(() => {
    const columnsToShow = [
      {
        field: 'date',
        textAlign: 'left',
        title: t('shop.date'),
        width: 150,
        sortable: false,
        template: ({ date }) =>
          renderToStaticMarkup(<>{formatDate(date, 'DD MMM YYYY')}</>),
      },
      {
        field: 'coins',
        textAlign: 'left',
        title: t('rewards_coins'),
        width: 150,
        sortable: false,
        template: ({ coins }) =>
          renderToStaticMarkup(<span>{coins || '-'}</span>),
      },
    ];

    if (isCharityCoinsEnabled) {
      columnsToShow.push({
        field: 'charityCoins',
        textAlign: 'left',
        title: t('rewards_charityCoins'),
        width: 150,
        sortable: false,
        template: ({ charityCoins }) =>
          renderToStaticMarkup(<span>{charityCoins || '-'}</span>),
      });
    }

    columnsToShow.push({
      field: 'from',
      textAlign: 'left',
      title: t('rewards_from'),
      width: 250,
      sortable: false,
      template: item =>
        renderToStaticMarkup(<span>{renderFromInformation(t, item)}</span>),
    });
    return columnsToShow;
  }, [isCharityCoinsEnabled]);

  const handleExportList = useCallback(() => {
    let endpoint =
      '/excel/analytics/wallet/transactions/list/xlsx?type=coins&transactionType=payment';
    if (filters?.endDate) {
      endpoint += `&endDate=${filters?.endDate}`;
    }
    if (filters?.startDate) {
      endpoint += `&startDate=${filters?.startDate}`;
    }
    window.open(endpoint, '_blank');
  }, [filters]);

  return (
    <div className="my-coins-container">
      <div className="header-container">
        <h5>{t('coins.myCoins')}</h5>
        <button
          type="button"
          onClick={handleExportList}
          className="btn btn-excel-export"
        >
          {t('export.excel')}
        </button>
      </div>
      <ServerSideList
        columns={columns}
        id="myCoinsList"
        dataSourceURL="/table/analytics/wallet/transactions/list?type=coins&transactionType=payment"
        dataSourceHeaders={{
          'Accept-Version': '1.0.0',
          'X-Gamification-API': 'Games',
        }}
        dataSourceParams={{
          populate: [
            'marketCommissionReport',
            'marketCommissionCryptoReport',
            'marketCommission',
            'survey',
            'game',
            'journey',
            'session',
            'marketOrder',
            'academyOrder',
          ],
        }}
        query={filters}
      />
    </div>
  );
}

CoinsEvolution.propTypes = {
  t: PropTypes.func.isRequired,
  filters: PropTypes.shape({
    startDate: PropTypes.number,
    endDate: PropTypes.number,
  }).isRequired,
};

export default translate(CoinsEvolution);
